import React from 'react';
import { ColumnDirective, ColumnsDirective, GridComponent, Inject, Selection, Sort, SortSettingsModel } from '@syncfusion/ej2-react-grids';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import SystemActions from '@shared/actions/SystemActions';
import { ApiError, displayApiError } from '@shared/api/apiClient';
import dataSourcesApi from '../api/dataSourcesApi';
import { DataConnectionType, DataSourceFileModel, DataSourceFileUploadConflictBehavior } from '../types';
import OperationProgressDialog from '../../components/OperationProgressDialog';

interface DataSourceFileBrowserDialogProps {
    type: DataConnectionType;
    visible: boolean;
    onSelect: (path: string) => void;
    onClose: () => void;
}

const sortSettings: SortSettingsModel = {
    columns: [{ field: 'Name', direction: 'Ascending' }],
    allowUnsort: false,
};

const getAcceptedExtensions = (type: DataConnectionType) => {
    switch (type) {
        case DataConnectionType.Csv:
            return '.csv,.txt';
        case DataConnectionType.Excel:
            return '.xls,.xlsx,.xlsm,.xlsb';
        case DataConnectionType.MsAccess:
            return '.mdb,.accdb';
        default:
            return '';
    }
};

const formatSize = (value: number) => {
    if (value < 1024) {
        return `${value} B`;
    }

    const units = ['KB', 'MB', 'GB'];
    let size = value / 1024;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex++;
    }

    return `${size.toFixed(size >= 10 ? 0 : 1)} ${units[unitIndex]}`;
};

const isDuplicateUploadError = (error: Error | ApiError | unknown) => {
    if (!(error instanceof ApiError)) {
        return false;
    }

    return error.propertyName?.toLowerCase() === 'file' && error.message.toLowerCase().includes('exists');
};

const DataSourceFileBrowserDialog: React.FC<DataSourceFileBrowserDialogProps> = ({
    type,
    visible,
    onSelect,
    onClose,
}) => {
    const [files, setFiles] = React.useState<DataSourceFileModel[]>([]);
    const [selectedFile, setSelectedFile] = React.useState<DataSourceFileModel | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [uploading, setUploading] = React.useState(false);
    const [deleting, setDeleting] = React.useState(false);
    const [conflictFile, setConflictFile] = React.useState<File | null>(null);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const gridRef = React.useRef<GridComponent | null>(null);
    const selectedPathRef = React.useRef<string | null>(null);

    const loadFiles = React.useCallback(async (preferredPath?: string) => {
        setLoading(true);
        setConflictFile(null);
        try {
            const nextFiles = await dataSourcesApi.getFiles(type);
            setFiles(nextFiles);
            const nextSelectedPath = preferredPath ?? selectedPathRef.current;
            const nextSelection = nextSelectedPath
                ? nextFiles.find(file => file.Path === nextSelectedPath) || null
                : null;
            selectedPathRef.current = nextSelection?.Path ?? null;
            setSelectedFile(nextSelection);
        } catch (error) {
            displayApiError(error);
        } finally {
            setLoading(false);
        }
    }, [type]);

    React.useEffect(() => {
        if (visible) {
            selectedPathRef.current = null;
            setSelectedFile(null);
            void loadFiles();
        }
    }, [loadFiles, type, visible]);

    React.useEffect(() => {
        gridRef.current?.refresh();
    }, [files]);

    const upload = React.useCallback(async (file: File, conflictBehavior: DataSourceFileUploadConflictBehavior) => {
        setUploading(true);
        try {
            const result = await dataSourcesApi.uploadFile(type, file, conflictBehavior);
            setConflictFile(null);
            await loadFiles(result.File.Path);
        } catch (error) {
            if (conflictBehavior === 'Reject' && isDuplicateUploadError(error)) {
                setConflictFile(file);
            } else {
                displayApiError(error);
            }
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    }, [loadFiles, type]);

    const onUploadChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            void upload(file, 'Reject');
        }
    }, [upload]);

    const deleteFile = React.useCallback((file: DataSourceFileModel) => {
        if (file.Referenced) {
            return;
        }

        SystemActions.PublicActions.displayConfirmationMessage({
            message: InovxLoader.t('facility:dataSources.files.confirmDelete', { name: file.Name }),
            confirmCallback: async () => {
                setDeleting(true);
                try {
                    await dataSourcesApi.deleteFile(type, file.Name);
                    await loadFiles();
                } catch (error) {
                    displayApiError(error);
                } finally {
                    setDeleting(false);
                }
            },
        });
    }, [loadFiles, type]);

    const onRowSelected = React.useCallback((args: any) => {
        const file = args.data as DataSourceFileModel;
        selectedPathRef.current = file.Path;
        setSelectedFile(file);
    }, []);

    const restoreGridSelection = React.useCallback(() => {
        if (!selectedPathRef.current) {
            return;
        }

        const visibleRows = (gridRef.current?.getCurrentViewRecords?.() as DataSourceFileModel[] | undefined) ?? files;
        const selectedIndex = visibleRows.findIndex(file => file.Path === selectedPathRef.current);
        if (selectedIndex >= 0) {
            gridRef.current?.selectRow(selectedIndex);
        }
    }, [files]);

    const closeDialog = React.useCallback((event?: React.MouseEvent<HTMLButtonElement>) => {
        event?.preventDefault();
        event?.stopPropagation();
        onClose();
    }, [onClose]);

    const selectFile = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (selectedFile) {
            onSelect(selectedFile.Path);
        }
    }, [onSelect, selectedFile]);

    const renderName = React.useCallback((row: DataSourceFileModel) => (
        <span className="data-sources-file-name-cell" title={row.Name}>{row.Name}</span>
    ), []);

    const renderSize = React.useCallback((row: DataSourceFileModel) => (
        <span>{formatSize(row.SizeBytes)}</span>
    ), []);

    const renderLastModified = React.useCallback((row: DataSourceFileModel) => (
        <span>{row.LastModified ? new Date(row.LastModified).toLocaleString() : ''}</span>
    ), []);

    const renderReferenced = React.useCallback((row: DataSourceFileModel) => (
        <span className={`data-sources-file-reference ${row.Referenced ? 'referenced' : ''}`}>
            {row.Referenced ? InovxLoader.t('facility:dataSources.files.inUse') : ''}
        </span>
    ), []);

    const renderActions = React.useCallback((row: DataSourceFileModel) => {
        const disabled = row.Referenced || deleting || loading || uploading;
        const title = row.Referenced
            ? InovxLoader.t('facility:dataSources.files.cannotDeleteReferenced')
            : InovxLoader.t('facility:dataSources.files.deleteFile');

        return (
            <div className="kb-action-buttons data-sources-file-grid-actions">
                <button
                    type="button"
                    className="e-btn e-flat"
                    title={title}
                    disabled={disabled}
                    onClick={event => {
                        event.preventDefault();
                        event.stopPropagation();
                        void deleteFile(row);
                    }}
                >
                    <span className="e-btn-icon e-icons e-delete data-sources-file-grid-action-button"></span>
                </button>
            </div>
        );
    }, [deleteFile, deleting, loading, uploading]);

    const canSelect = !!selectedFile && !loading && !uploading;
    const showGrid = files.length > 0;
    const footerTemplate = React.useCallback(() => (
        <div className="data-sources-file-footer">
            <button type="button" className="e-btn e-primary" disabled={!canSelect} onClick={selectFile}>
                {InovxLoader.t('facility:dataSources.files.select')}
            </button>
            <button type="button" className="e-btn" disabled={uploading} onClick={closeDialog}>
                {InovxLoader.t('facility:dataSources.files.cancel')}
            </button>
        </div>
    ), [canSelect, closeDialog, selectFile, uploading]);

    return (
        <>
            <DialogComponent
                target="#app"
                cssClass="data-sources-file-browser-dialog"
                visible={visible}
                isModal={true}
                showCloseIcon={!uploading}
                closeOnEscape={!uploading}
                width="780px"
                header={InovxLoader.t('facility:dataSources.files.selectFile')}
                close={() => {
                    if (!uploading) {
                        onClose();
                    }
                }}
                footerTemplate={footerTemplate}
                zIndex={110001}
            >
                <div className="data-sources-file-browser">
                    <div className="data-sources-file-toolbar">
                        <button
                            type="button"
                            className="e-btn e-flat data-sources-file-icon-button"
                            title={uploading ? InovxLoader.t('facility:dataSources.files.uploading') : InovxLoader.t('facility:dataSources.files.upload')}
                            disabled={uploading || loading}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <span className="e-btn-icon e-icons e-upload-1"></span>
                        </button>
                        <button
                            type="button"
                            className="e-btn e-flat data-sources-file-icon-button"
                            title={InovxLoader.t('facility:dataSources.files.refresh')}
                            disabled={uploading || loading}
                            onClick={() => loadFiles(selectedPathRef.current ?? undefined)}
                        >
                            <span className="e-btn-icon e-icons e-refresh"></span>
                        </button>
                        {loading && showGrid && (
                            <span className="data-sources-file-toolbar-status">{InovxLoader.t('facility:dataSources.files.loading')}</span>
                        )}
                        <input
                            ref={fileInputRef}
                            type="file"
                            className="data-sources-hidden-file-input"
                            style={{ display: 'none' }}
                            accept={getAcceptedExtensions(type)}
                            onChange={onUploadChange}
                        />
                    </div>

                    {conflictFile && (
                        <div className="data-sources-file-conflict">
                            <span>{InovxLoader.t('facility:dataSources.files.fileAlreadyExists', { name: conflictFile.name })}</span>
                            <button type="button" className="e-btn" disabled={uploading} onClick={() => setConflictFile(null)}>
                                {InovxLoader.t('facility:dataSources.files.cancelUpload')}
                            </button>
                            <button type="button" className="e-btn" disabled={uploading} onClick={() => upload(conflictFile, 'Overwrite')}>
                                {InovxLoader.t('facility:dataSources.files.overwrite')}
                            </button>
                            <button type="button" className="e-btn" disabled={uploading} onClick={() => upload(conflictFile, 'AutoRename')}>
                                {InovxLoader.t('facility:dataSources.files.autoRename')}
                            </button>
                        </div>
                    )}

                    {loading && !showGrid ? (
                        <div className="data-sources-file-state">{InovxLoader.t('facility:dataSources.files.loading')}</div>
                    ) : !showGrid ? (
                        <div className="data-sources-file-state">{InovxLoader.t('facility:dataSources.files.noFilesFound')}</div>
                    ) : (
                        <GridComponent
                            ref={grid => { gridRef.current = grid; }}
                            className="data-sources-file-grid"
                            dataSource={files}
                            allowSorting={true}
                            sortSettings={sortSettings}
                            allowSelection={true}
                            selectionSettings={{ type: 'Single' }}
                            rowSelected={onRowSelected}
                            dataBound={restoreGridSelection}
                            locale={typeof InovxLoader !== 'undefined' ? InovxLoader.getSyncfusionLocale() : 'en-US'}
                        >
                            <ColumnsDirective>
                                <ColumnDirective field="Name" headerText={InovxLoader.t('facility:dataSources.files.fileName')} width="32%" template={renderName} />
                                <ColumnDirective field="SizeBytes" headerText={InovxLoader.t('facility:dataSources.files.size')} width="14%" template={renderSize} />
                                <ColumnDirective field="LastModified" headerText={InovxLoader.t('facility:dataSources.files.lastModified')} width="23%" template={renderLastModified} />
                                <ColumnDirective field="Extension" headerText={InovxLoader.t('facility:dataSources.files.extension')} width="12%" />
                                <ColumnDirective field="Referenced" headerText={InovxLoader.t('facility:dataSources.files.inUse')} width="13%" template={renderReferenced} />
                                <ColumnDirective headerText="" width="70" allowSorting={false} template={renderActions} />
                            </ColumnsDirective>
                            <Inject services={[Selection, Sort]} />
                        </GridComponent>
                    )}

                </div>
            </DialogComponent>
            <OperationProgressDialog
                visible={uploading}
                message={InovxLoader.t('facility:dataSources.files.uploading')}
            />
        </>
    );
};

export default DataSourceFileBrowserDialog;
